import { useCallback } from "react";
import useSWR from "swr";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import { Paginated, PaginationQuery } from "../types";
import {
  AccessRule,
  AccessRuleImport,
  AccessTarget,
  AppSettings,
  ImportAccessRulesBody,
  UpsertAccessRuleBody,
} from "./types/accessControl";

const targetUrl = (target: AccessTarget) =>
  "scope" in target ? `/admin/access/${target.scope}` : `/admin/access/modules/${encodeURIComponent(target.moduleId)}`;
const settingsUrl = (target: AccessTarget) => `${targetUrl(target)}/settings`;
const rulesUrl = (target: AccessTarget) => `${targetUrl(target)}/rules`;
const importRulesUrl = (target: AccessTarget) => `${rulesUrl(target)}/import`;

// The app or the module the rule is of.
export const getAccessRuleTarget = (rule: AccessRule): AccessTarget =>
  rule.scope ? { scope: rule.scope } : { moduleId: rule.moduleId ?? "" };

// A key identifying the app or the module, e.g. of a list or an element.
export const getAccessTargetKey = (target: AccessTarget): string =>
  "scope" in target ? `app:${target.scope}` : `module:${target.moduleId}`;

export const useAccessControlApi = () => {
  const api = useApi();

  const getSettings = useCallback(
    (target: AccessTarget, signal: AbortSignal) => {
      const url = settingsUrl(target);
      return useSWR<AppSettings, CustomError, string>(url, () => api.get<AppSettings>({ url, signal }));
    },
    [api],
  );

  const updateSettings = useCallback(
    async (target: AccessTarget, body: AppSettings): Promise<AppSettings> => {
      const url = settingsUrl(target);
      const item = await api.putWithResult<AppSettings, AppSettings>(url, body);
      mutateCache([url]);
      return item;
    },
    [api],
  );

  const getRules = useCallback(
    (target: AccessTarget, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(rulesUrl(target), query);
      return useSWR<Paginated<AccessRule>, CustomError, string>(url, () =>
        api.get<Paginated<AccessRule>>({ url, signal }),
      );
    },
    [api],
  );

  const addRule = useCallback(
    async (target: AccessTarget, body: UpsertAccessRuleBody): Promise<AccessRule> => {
      const url = rulesUrl(target);
      const item = await api.post<AccessRule, UpsertAccessRuleBody>(url, body);
      mutateCache([url]);
      return item;
    },
    [api],
  );

  const removeRule = useCallback(
    async (target: AccessTarget, ruleId: string): Promise<void> => {
      const url = rulesUrl(target);
      await api.del(`${url}/${ruleId}`);
      mutateCache([url]);
    },
    [api],
  );

  // No request until the app or module to import from is chosen.
  const getRulesToImport = useCallback(
    (target: AccessTarget, from: AccessTarget | undefined, signal: AbortSignal) => {
      const url = !from
        ? null
        : "scope" in from
          ? `${importRulesUrl(target)}?from=${from.scope}`
          : `${importRulesUrl(target)}?fromModule=${encodeURIComponent(from.moduleId)}`;
      return useSWR<AccessRuleImport[], CustomError, string | null>(url, (url: string) =>
        api.get<AccessRuleImport[]>({ url, signal }),
      );
    },
    [api],
  );

  const importRules = useCallback(
    async (target: AccessTarget, from: AccessTarget, emails: string[]): Promise<void> => {
      const body: ImportAccessRulesBody =
        "scope" in from ? { fromScope: from.scope, emails } : { fromModuleId: from.moduleId, emails };
      await api.postNoResult<ImportAccessRulesBody>(importRulesUrl(target), body);
      mutateCache([rulesUrl(target), importRulesUrl(target)]);
    },
    [api],
  );

  return {
    getSettings,
    updateSettings,
    getRules,
    addRule,
    removeRule,
    getRulesToImport,
    importRules,
  };
};

export default useAccessControlApi;
